/**
 * Terms and Conditions Detail Page (read-only display with formatted content)
 */
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Divider,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon } from '@mui/icons-material';
import { PageHeader } from '../../components/common';
import { useGetTermsAdminDetailQuery } from '../../store/api/termsApi';
import { usePermissions } from '../../hooks/usePermissions';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const TermsDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('manage_ads');

  const { data, isLoading, error } = useGetTermsAdminDetailQuery(Number(id!), {
    skip: !id,
  });

  const terms = data?.data;

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !terms) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', py: 10 }}>
        <Typography color="error" variant="h6" gutterBottom>
          {t('terms.empty')}
        </Typography>
        <Button onClick={() => navigate('/terms')} sx={{ mt: 2 }} startIcon={<ArrowBackIcon />}>
          {t('terms.buttons.backToTerms')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={terms.title}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.terms'), path: '/terms' },
          { label: terms.title },
        ]}
        action={
          canManage ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/terms/${terms.id}/edit`)}
            >
              {t('common.edit')}
            </Button>
          ) : undefined
        }
      />

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/terms')}
        sx={{ mb: 3 }}
      >
        {t('terms.buttons.backToTerms')}
      </Button>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="overline" color="text.secondary">
              {t('terms.detail.lastUpdated')}: {moment(terms.updated_at).format('MMM DD, YYYY HH:mm')}
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Box
            sx={{
              '& h1': { fontSize: '1.75rem', fontWeight: 700, mt: 2, mb: 1 },
              '& h2': { fontSize: '1.5rem', fontWeight: 600, mt: 2, mb: 1 },
              '& h3': { fontSize: '1.25rem', fontWeight: 600, mt: 1.5, mb: 0.5 },
              '& ul, & ol': { pl: 3, my: 1 },
              '& p': { mb: 1.5 },
              '& a': { color: 'primary.main' },
            }}
            dangerouslySetInnerHTML={{ __html: terms.terms || `<p>${t('terms.detail.noContent')}</p>` }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default TermsDetailPage;
